import clsx from 'clsx';
import noop from 'lodash/noop';
import { MouseEventHandler, PropsWithChildren, useMemo } from 'react';

import { cn } from 'utils/cn';

import { LoadingSpinnerIcon, LoadingSpinnerSmallIcon } from '../../icons/LoadingIcon';
import { LockIcon } from '../../icons/LockIcon';
import { focusVisibleClass } from '../classNames';

export type ButtonProps = PropsWithChildren<{
  onClick: MouseEventHandler<HTMLButtonElement>;
  isLoading?: boolean;
  className?: string;
  innerClassName?: string;
  disabled?: boolean;
  isLocked?: boolean;
  size?: 'small' | 'medium' | 'large';
  loaderClassName?: string;
  isCta?: boolean;
}>;

export const BaseButton: React.FC<ButtonProps> = ({
  children,
  className,
  innerClassName,
  disabled,
  isLoading,
  isLocked,
  size = 'small',
  loaderClassName,
  onClick,
  isCta = false
}) => {
  const variantClassName = useMemo(() => {
    switch (size) {
      case 'small':
        return cn('py-2 px-4 text-subheading-6', isCta && 'text-heading-6');
      case 'medium':
        return cn('py-2.5 px-6 text-subheading-6', isCta && 'text-heading-6');
      case 'large':
        return cn('py-3 px-7 text-subheading-5', isCta && 'text-heading-5');
    }
  }, [size, isCta]);
  return (
    <button
      className={cn(clsx('flex justify-center rounded-[10px] relative', focusVisibleClass, variantClassName), className)}
      onClick={isLoading ? noop : onClick}
      disabled={disabled}
    >
      {isLocked && (
        <div className="absolute top-1 right-1">
          <LockIcon />
        </div>
      )}
      <div className={cn('flex justify-center items-center gap-2', isLoading ? 'invisible' : 'visible', innerClassName)}>{children}</div>
      {isLoading && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          {size === 'small' || size === 'medium' ? <LoadingSpinnerSmallIcon className={loaderClassName} /> : <LoadingSpinnerIcon className={loaderClassName} />}
        </div>
      )}
    </button>
  );
};
